// Pro forma issue confirm: every print / PDF / download / send of the Pro Forma template asks
// whether to move the quote to Accepted (not paid). System changes are written to Staff notes.
import { useState } from "react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { canonicalStatus } from "../quoteStatus";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
} from "../../../components/ui/alert-dialog";

const MOVABLE = new Set(["Draft", "Sent", "Expired"]);
const HOW = { printed: "printed", pdf: "saved as PDF", download_pdf: "downloaded as PDF", download_html: "downloaded as HTML", sent: "sent", manual: "marked as issued" };
export const proformaRef = (ref) => (/^CWQ/i.test(ref || "") ? `CWP${String(ref).slice(3)}` : `CWP-${ref || ""}`);

export function useProformaIssue(quote, onDone) {
  const [pending, setPending] = useState(null);
  const status = quote?.Status === "Archived" ? "Archived" : canonicalStatus(quote?.Status, { payment: quote?.Payment, sent: quote?.Sent });
  const record = async (how, accept) => {
    try {
      const { data } = await api.post(`/quotes/${quote._id}/proforma-issued`, { how, accept });
      if (data.changed) toast.success(`${data.proforma_ref} issued · status ${data.status_before} → Accepted (not paid)`, { description: "Logged in Staff notes" });
      else if (data.warning) toast.warning(data.warning);
      else toast.success(`${data.proforma_ref} ${HOW[how]} · logged in Staff notes`);
      onDone?.(data);
    } catch { toast.error("Couldn't record the pro forma"); }
  };
  const trigger = (how) => { if (quote?._id) setPending(how); };
  const close = () => setPending(null);
  const dialog = (
    <AlertDialog open={!!pending} onOpenChange={(o) => !o && close()}>
      <AlertDialogContent className="bg-[#0B1220] border-white/10 text-slate-100" data-testid="proforma-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Pro forma {proformaRef(quote?.QuoteRef)} {HOW[pending] || ""}</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-400">
            {MOVABLE.has(status)
              ? <>This quote is <b className="text-slate-200">{status}</b>. A pro forma means the customer has agreed — move it to <b className="text-emerald-300">Accepted (not paid)</b>? The change is written to Staff notes.</>
              : status === "Rejected" || status === "Archived"
                ? <>This quote is <b className="text-rose-300">{status}</b>, so its status won't change. Reopen it first if the customer has now agreed. The pro forma issue will still be logged.</>
                : <>This quote is already <b className="text-slate-200">{status}</b>. The status stays as it is; the pro forma issue is logged in Staff notes.</>}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <button onClick={close} data-testid="proforma-confirm-cancel" className="px-3 py-2 rounded-lg text-[12.5px] text-slate-400 hover:text-slate-200">Cancel</button>
          <button onClick={() => { const h = pending; close(); record(h, false); }} data-testid="proforma-confirm-log-only"
            className="px-3 py-2 rounded-lg border border-white/15 text-[12.5px] text-slate-200 hover:bg-white/5">
            {MOVABLE.has(status) ? "Keep status, just log it" : "Log it"}
          </button>
          {MOVABLE.has(status) && (
            <button onClick={() => { const h = pending; close(); record(h, true); }} data-testid="proforma-confirm-accept"
              className="px-3 py-2 rounded-lg bg-emerald-500 text-black font-semibold text-[12.5px] hover:bg-emerald-400">
              Mark Accepted (not paid)
            </button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return { trigger, dialog };
}

export function ProformaMarkButton({ quote, onDone, testid }) {
  const pf = useProformaIssue(quote, onDone);
  return <>
    <button onClick={() => pf.trigger("manual")} data-testid={testid} title="Record that this pro forma was printed or sent"
      className="text-[11px] px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 inline-flex items-center gap-1">
      Mark pro forma issued
    </button>
    {pf.dialog}
  </>;
}
